/**
 * Fantasy Console 0 - Thumbnail Service
 *
 * Handles thumbnail generation and upload to Supabase storage.
 */

import { supabase } from '../lib/supabase'

/**
 * Thumbnail service for capturing and uploading cart previews
 */
export const ThumbnailService = {
  /**
   * Capture console canvas as WebP blob
   * @param canvas The console canvas element
   * @returns Blob of the captured image
   */
  async captureFromCanvas(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Failed to create thumbnail blob'))
          }
        },
        'image/webp',
        0.8,
      )
    })
  },

  /**
   * Upload thumbnail to Supabase storage
   * @param cartId The cart ID (used as filename)
   * @param blob The thumbnail blob
   * @returns Public URL of the uploaded thumbnail
   */
  async uploadThumbnail(cartId: string, blob: Blob): Promise<string> {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    const fileName = `${cartId}.webp`

    const { error } = await supabase.storage
      .from('cart-thumbnails')
      .upload(fileName, blob, {
        contentType: 'image/webp',
        upsert: true,
      })

    if (error) {
      console.error('Failed to upload thumbnail:', error)
      throw new Error(`Failed to upload thumbnail: ${error.message}`)
    }

    // Get public URL
    const { data } = supabase.storage
      .from('cart-thumbnails')
      .getPublicUrl(fileName)

    return data.publicUrl
  },

  /**
   * Generate and upload thumbnail for a cart
   * @param cartId The cart ID
   * @param consoleCanvas The console canvas element
   * @returns Public URL of the uploaded thumbnail
   */
  async generateAndUpload(
    cartId: string,
    consoleCanvas: HTMLCanvasElement,
  ): Promise<string> {
    const blob = await this.captureFromCanvas(consoleCanvas)
    return this.uploadThumbnail(cartId, blob)
  },

  /**
   * Delete thumbnail from Supabase storage
   * @param cartId The cart ID (used as filename)
   */
  async deleteThumbnail(cartId: string): Promise<void> {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    const fileName = `${cartId}.webp`

    const { error } = await supabase.storage
      .from('cart-thumbnails')
      .remove([fileName])

    if (error) {
      console.error('Failed to delete thumbnail:', error)
      // Don't throw - thumbnail deletion failure is not critical
    }
  },

  /**
   * Capture from a data URL and convert to a Blob
   * @param dataUrl The data URL from canvas capture
   * @returns Blob of the captured image
   */
  async captureFromDataUrl(dataUrl: string): Promise<Blob> {
    // Convert data URL to blob
    const response = await fetch(dataUrl)
    const blob = await response.blob()

    // If already webp, return as-is
    if (blob.type === 'image/webp') {
      return blob
    }

    // Convert to webp using canvas
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('Failed to get canvas context'))
          return
        }
        ctx.drawImage(img, 0, 0)
        canvas.toBlob(
          (webpBlob) => {
            if (webpBlob) {
              resolve(webpBlob)
            } else {
              reject(new Error('Failed to create thumbnail blob'))
            }
          },
          'image/webp',
          0.8,
        )
      }
      img.onerror = () =>
        reject(new Error('Failed to load image from data URL'))
      img.src = dataUrl
    })
  },

  /**
   * Generate and upload thumbnail from a data URL
   * @param cartId The cart ID
   * @param dataUrl The canvas data URL
   * @returns Public URL of the uploaded thumbnail
   */
  async generateAndUploadFromDataUrl(
    cartId: string,
    dataUrl: string,
  ): Promise<string> {
    const blob = await this.captureFromDataUrl(dataUrl)
    return this.uploadThumbnail(cartId, blob)
  },
}
